import { forwardRef, type ReactNode } from 'react';
import { AiMagicIcon, Cancel01Icon, Tick01Icon } from '@terra/ui-icons';
import { Box, HStack, css } from '@terra/ui-styled-system';
import { Button } from './button';
import { IconButton } from './icon-button';
import { MetaDefault } from './typography';

export const FIELD_COPILOT_KINDS = [
  'suggested',
  'affected',
  'accepted',
] as const;

export type FieldCopilotKind = (typeof FIELD_COPILOT_KINDS)[number];

export type FieldCopilotNoteProps = {
  kind: FieldCopilotKind;
  from?: string;
  to?: string;
  rewritten?: boolean;
  acceptAriaLabel?: string;
  rejectAriaLabel?: string;
  whyLabel?: string;
  showPreviousLabel?: string;
  rewrittenLabel?: string;
  onAccept?: () => void;
  onReject?: () => void;
  onWhy?: () => void;
  onShowPrevious?: () => void;
};

const NoteTextButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick?: () => void;
}) => (
  <Button
    type="button"
    variant="link"
    size="xs"
    onClick={onClick}
    className={css({
      flexShrink: 0,
      height: 'auto',
      minHeight: 'unset',
      px: 0,
    })}
  >
    {label}
  </Button>
);

const CopilotMark = () => (
  <Box
    color="fg.accent.default"
    flexShrink={0}
    className={css({
      width: '12px',
      height: '12px',
      '& svg': { width: '12px', height: '12px' },
    })}
  >
    <AiMagicIcon />
  </Box>
);

const ChangeText = ({
  from,
  to,
  rewritten,
  rewrittenLabel,
}: {
  from?: string;
  to?: string;
  rewritten?: boolean;
  rewrittenLabel: string;
}) => {
  if (rewritten) {
    return <MetaDefault color="fg.default">{rewrittenLabel}</MetaDefault>;
  }

  if (from && to) {
    return (
      <HStack gap={1.5} alignItems="center" minW={0}>
        <MetaDefault
          color="fg.muted"
          className={css({ textDecoration: 'line-through' })}
        >
          {from}
        </MetaDefault>
        <MetaDefault color="fg.muted">→</MetaDefault>
        <MetaDefault color="fg.default">{to}</MetaDefault>
      </HStack>
    );
  }

  if (to) {
    return <MetaDefault color="fg.default">{to}</MetaDefault>;
  }

  return null;
};

export const FieldCopilotNote = forwardRef<
  HTMLDivElement,
  FieldCopilotNoteProps
>(function FieldCopilotNote(props, ref) {
  const {
    kind,
    from,
    to,
    rewritten,
    acceptAriaLabel = 'Accept',
    rejectAriaLabel = 'Reject',
    whyLabel = 'why',
    showPreviousLabel = 'Show previous',
    rewrittenLabel = 'Rewritten',
    onAccept,
    onReject,
    onWhy,
    onShowPrevious,
  } = props;

  let trailing: ReactNode = null;

  switch (kind) {
    case 'suggested':
      trailing = (
        <HStack gap={1} alignItems="center" flexShrink={0}>
          <IconButton
            type="button"
            variant="ghost"
            size="3xs"
            aria-label={rejectAriaLabel}
            onClick={onReject}
          >
            <Cancel01Icon />
          </IconButton>
          <IconButton
            type="button"
            variant="ghost"
            size="3xs"
            aria-label={acceptAriaLabel}
            onClick={onAccept}
          >
            <Tick01Icon />
          </IconButton>
        </HStack>
      );
      break;
    case 'affected':
      trailing = <NoteTextButton label={whyLabel} onClick={onWhy} />;
      break;
    case 'accepted':
      trailing = null;
      break;
    default: {
      const _exhaustive: never = kind;

      return _exhaustive;
    }
  }

  const showSpacer = kind === 'suggested' || kind === 'affected';

  return (
    <HStack
      ref={ref}
      gap={1.5}
      alignItems="center"
      w="full"
      minH={rewritten || kind === 'suggested' ? '20px' : '16px'}
    >
      <CopilotMark />
      <ChangeText
        from={from}
        to={to}
        rewritten={rewritten}
        rewrittenLabel={rewrittenLabel}
      />
      {rewritten && onShowPrevious ? (
        <NoteTextButton label={showPreviousLabel} onClick={onShowPrevious} />
      ) : null}
      {showSpacer ? <Box flex="1" minW={0} /> : null}
      {trailing}
    </HStack>
  );
});

FieldCopilotNote.displayName = 'Field.CopilotNote';
